import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import DashboardNav from "../components/DashboardNav";
import CampaignCard from "../components/CampaignCard";
import {
  Plus,
  Gamepad2,
  DollarSign,
  Users,
  Link as LinkIcon,
  Copy,
  ExternalLink,
  Eye,
  AlertCircle,
  Pencil,
} from "lucide-react";

function DashboardPage() {
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [joinedCampaigns, setJoinedCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);

  useEffect(() => {
    if (userProfile?.role === "admin") {
      navigate("/admin", { replace: true });
      return;
    }
  }, [userProfile, navigate]);

  useEffect(() => {
    async function fetchCampaigns() {
      if (!currentUser) return;
      try {
        const q = query(
          collection(db, "campaigns"),
          where("publisherId", "==", currentUser.uid)
        );
        const snap = await getDocs(q);
        setCampaigns(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error("Failed to fetch campaigns:", err);
      }
      setLoadingCampaigns(false);
    }

    async function fetchJoinedCampaigns() {
      if (!currentUser) return;
      try {
        const q = query(
          collection(db, "campaignJoins"),
          where("influencerId", "==", currentUser.uid)
        );
        const snap = await getDocs(q);
        const joins = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        joins.sort((a, b) => (b.joinedAt?.seconds || 0) - (a.joinedAt?.seconds || 0));
        setJoinedCampaigns(joins);
      } catch (err) {
        console.error("Failed to fetch joined campaigns:", err);
      }
      setLoadingCampaigns(false);
    }

    if (userProfile?.role === "publisher") {
      fetchCampaigns();
    } else if (userProfile?.role === "influencer") {
      fetchJoinedCampaigns();
    } else {
      setLoadingCampaigns(false);
    }
  }, [currentUser, userProfile]);

  const totalBudget = campaigns.reduce((s, c) => s + (c.maxBudget || 0), 0);
  const totalSpent = campaigns.reduce((s, c) => s + (c.totalSpent || 0), 0);
  const totalWishlists = campaigns.reduce((s, c) => s + (c.totalWishlists || 0), 0);

  // Influencer stats from joinedCampaigns
  const infTotalWishlists = joinedCampaigns.reduce((s, j) => s + (j.wishlists || 0), 0);
  const infTotalPurchases = joinedCampaigns.reduce((s, j) => s + (j.purchases || 0), 0);
  const infTotalEarned = joinedCampaigns.reduce((s, j) => {
    if (j.tierSpend != null) return s + j.tierSpend;
    if (j.campaignType === "purchase") {
      return s + (j.purchases || 0) * (j.pricePerPurchase || 0);
    }
    return s + (j.wishlists || 0) * (j.pricePerWishlist || 0);
  }, 0);
  const infTotalVisits = joinedCampaigns.reduce((s, j) => s + (j.visits || 0), 0);

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <DashboardNav />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-start justify-between mb-10">
          <div>
            <h1 className="text-3xl font-bold mb-1">
              Welcome back, {currentUser?.displayName || "there"}
            </h1>
            <p className="text-gray-400">
              {userProfile?.role === "publisher"
                ? "Manage your campaigns and track influencer performance."
                : "Browse campaigns and track your earnings."}
            </p>
          </div>
          {userProfile?.role === "publisher" && (
            <Link
              to="/campaigns/create"
              className="flex items-center gap-2 bg-brand-600 hover:bg-brand-500 text-white px-5 py-2.5 rounded-xl transition-all font-semibold text-sm shadow-lg shadow-brand-600/25"
            >
              <Plus className="w-4 h-4" />
              New Campaign
            </Link>
          )}
          {userProfile?.role === "influencer" && (
            <Link
              to="/links/edit"
              className="flex items-center gap-2 bg-brand-600 hover:bg-brand-500 text-white px-5 py-2.5 rounded-xl transition-all font-semibold text-sm shadow-lg shadow-brand-600/25"
            >
              <LinkIcon className="w-4 h-4" />
              gamesin.bio Page
            </Link>
          )}
        </div>

        {/* Publisher Stats */}
        {userProfile?.role === "publisher" && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg bg-brand-500/10 flex items-center justify-center">
                  <Gamepad2 className="w-5 h-5 text-brand-400" />
                </div>
                <p className="text-sm text-gray-400">Active Campaigns</p>
              </div>
              <p className="text-3xl font-bold">
                {campaigns.filter((c) => c.status === "active").length}
              </p>
            </div>
            <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-green-400" />
                </div>
                <p className="text-sm text-gray-400">Total Wishlists</p>
              </div>
              <p className="text-3xl font-bold">{totalWishlists.toLocaleString()}</p>
            </div>
            <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-purple-400" />
                </div>
                <p className="text-sm text-gray-400">Total Spent</p>
              </div>
              <p className="text-3xl font-bold">${totalSpent.toLocaleString()}</p>
            </div>
          </div>
        )}

        {/* Influencer Stats */}
        {userProfile?.role === "influencer" && (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-lg bg-green-500/10 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-green-400" />
                  </div>
                  <p className="text-sm text-gray-400">Total Earned</p>
                </div>
                <p className="text-3xl font-bold">${infTotalEarned.toLocaleString()}</p>
              </div>
              <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-lg bg-brand-500/10 flex items-center justify-center">
                    <Gamepad2 className="w-5 h-5 text-brand-400" />
                  </div>
                  <p className="text-sm text-gray-400">Wishlists Generated</p>
                </div>
                <p className="text-3xl font-bold">{infTotalWishlists.toLocaleString()}</p>
              </div>
              <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-lg bg-purple-500/10 flex items-center justify-center">
                    <Users className="w-5 h-5 text-purple-400" />
                  </div>
                  <p className="text-sm text-gray-400">Total Visits</p>
                </div>
                <p className="text-3xl font-bold">{infTotalVisits.toLocaleString()}</p>
              </div>
            </div>

            <div className="flex gap-3 bg-yellow-500/5 border border-yellow-500/20 rounded-2xl p-4 mb-10">
              <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-300/80 leading-relaxed">
                <span className="font-semibold text-yellow-300">Earnings Payout Info:</span> Earnings are paid out when your balance reaches <span className="font-semibold text-yellow-300">$20 or more</span>. Payouts are processed only after all wishlists/purchases have been verified as genuine with no anomalies detected.
              </div>
            </div>
          </>
        )}

        {/* Campaigns List (Publisher) */}
        {userProfile?.role === "publisher" && (
          <div>
            <h2 className="text-xl font-semibold mb-4">My Campaigns</h2>

            {loadingCampaigns ? (
              <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-12 text-center">
                <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto" />
              </div>
            ) : campaigns.length === 0 ? (
              <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-12 text-center">
                <Gamepad2 className="w-12 h-12 text-gray-700 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2 text-gray-300">No campaigns yet</h3>
                <p className="text-gray-500 mb-6">
                  Create your first campaign and start connecting with influencers.
                </p>
                <Link
                  to="/campaigns/create"
                  className="inline-flex items-center gap-2 bg-brand-600 hover:bg-brand-500 text-white px-6 py-2.5 rounded-xl transition-all font-semibold text-sm"
                >
                  <Plus className="w-4 h-4" />
                  Create Campaign
                </Link>
              </div>
            ) : (
              <div className="grid gap-4">
                {campaigns.map((campaign) => (
                  <CampaignCard
                    key={campaign.id}
                    campaign={campaign}
                    linkTo={`/campaigns/${campaign.id}`}
                    actions={
                      <>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/campaigns/${campaign.id}/edit`);
                          }}
                          className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-xl transition-all font-semibold text-sm"
                        >
                          <Pencil className="w-4 h-4" />
                          Edit Campaign
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/campaigns/${campaign.id}`);
                          }}
                          className="flex items-center gap-2 bg-brand-600 hover:bg-brand-500 text-white px-4 py-2 rounded-xl transition-all font-semibold text-sm"
                        >
                          <Eye className="w-4 h-4" />
                          View Details / Upload CSV
                        </button>
                      </>
                    }
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Influencer Joined Campaigns */}
        {userProfile?.role === "influencer" && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">My Campaigns</h2>
              <Link
                to="/campaigns"
                className="flex items-center gap-2 text-sm text-brand-400 hover:text-brand-300 transition-colors"
              >
                Browse More
                <ExternalLink className="w-3.5 h-3.5" />
              </Link>
            </div>

            {loadingCampaigns ? (
              <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-12 text-center">
                <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto" />
              </div>
            ) : joinedCampaigns.length === 0 ? (
              <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-8 text-center">
                <Gamepad2 className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400 mb-4">
                  You haven't joined any campaigns yet.
                </p>
                <Link
                  to="/campaigns"
                  className="inline-flex items-center gap-2 bg-brand-600 hover:bg-brand-500 text-white px-6 py-2.5 rounded-xl transition-all font-semibold text-sm"
                >
                  Browse Campaigns
                </Link>
              </div>
            ) : (
              <div className="grid gap-4">
                {joinedCampaigns.map((join) => (
                  <div
                    key={join.id}
                    onClick={() => navigate(`/campaigns/${join.campaignId}/performance`)}
                    className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-5 hover:border-gray-700/50 transition-all cursor-pointer"
                  >
                    <div className="flex items-start gap-4">
                      {join.campaignCoverImageUrl && (
                        <img
                          src={join.campaignCoverImageUrl}
                          alt={join.campaignTitle}
                          className="w-16 h-16 rounded-xl object-cover flex-shrink-0"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <Link
                              to={`/campaigns/${join.campaignId}`}
                              className="font-semibold text-white hover:text-brand-400 transition-colors"
                            >
                              {join.campaignTitle}
                            </Link>
                            <p className="text-sm text-gray-500">
                              by {join.companyName || join.publisherName}
                            </p>
                          </div>
                          <span className="text-sm text-gray-500 flex-shrink-0">
                            {join.tierSpend != null ? "tiered pricing" : `$${join.campaignType === "purchase" ? join.pricePerPurchase : join.pricePerWishlist}/${join.campaignType === "purchase" ? "purchase" : "wishlist"}`}
                          </span>
                        </div>
                        {/* Per-campaign stats */}
                        {(join.wishlists > 0 || join.purchases > 0 || join.visits > 0) && (
                          <div className="flex flex-wrap gap-3 mt-2">
                            {join.campaignType === "purchase" ? (
                              <span className="text-xs text-brand-400 bg-brand-500/10 px-2 py-1 rounded-lg">
                                {(join.purchases || 0).toLocaleString()} purchases
                              </span>
                            ) : (
                              <span className="text-xs text-brand-400 bg-brand-500/10 px-2 py-1 rounded-lg">
                                {(join.wishlists || 0).toLocaleString()} wishlists
                              </span>
                            )}
                            {join.visits > 0 && (
                              <span className="text-xs text-blue-400 bg-blue-500/10 px-2 py-1 rounded-lg">
                                {join.visits.toLocaleString()} visits
                              </span>
                            )}
                            <span className="text-xs text-green-400 bg-green-500/10 px-2 py-1 rounded-lg">
                              ${(join.tierSpend != null
                                ? join.tierSpend
                                : join.campaignType === "purchase"
                                  ? (join.purchases || 0) * (join.pricePerPurchase || 0)
                                  : (join.wishlists || 0) * (join.pricePerWishlist || 0)
                              ).toLocaleString()} earned
                            </span>
                          </div>
                        )}
                        <div className="mt-3 flex gap-2 bg-orange-500/5 border border-orange-500/20 rounded-xl p-2.5">
                          <AlertCircle className="w-3.5 h-3.5 text-orange-400 flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-orange-300/80 leading-relaxed">
                            <span className="font-semibold text-orange-300">Submit every post or video</span> where you used your UTM link. Click "View Details" below and scroll down to add your posts — this is crucial for verifying your referrals.
                          </p>
                        </div>
                        <div className="mt-3 bg-gray-800/50 border border-gray-700/50 rounded-xl p-3">
                          <div className="flex items-center gap-2 mb-1">
                            <LinkIcon className="w-3.5 h-3.5 text-gray-500" />
                            <p className="text-xs text-gray-500">Your UTM Link</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <code className="text-xs text-brand-400 break-all flex-1">
                              {join.utmLink}
                            </code>
                            <button
                              type="button"
                              onClick={() => navigator.clipboard.writeText(join.utmLink)}
                              className="text-xs bg-gray-700 hover:bg-gray-600 text-white px-2.5 py-1 rounded-lg transition-colors flex-shrink-0 flex items-center gap-1"
                            >
                              <Copy className="w-3 h-3" />
                              Copy
                            </button>
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/campaigns/${join.campaignId}/performance`);
                          }}
                          className="mt-3 flex items-center gap-2 bg-brand-600 hover:bg-brand-500 text-white px-4 py-2 rounded-xl transition-all font-semibold text-sm w-fit"
                        >
                          <Eye className="w-4 h-4" />
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default DashboardPage;
